import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

const guideArticles = [
  { slug: 'oopbuy-spreadsheet-guide', labelKey: 'articles.guide1_title' },
  { slug: 'oopbuy-spreadsheet-shipping-guide', labelKey: 'articles.shipping1_title' },
  { slug: 'oopbuy-spreadsheet-qc-guide', labelKey: 'articles.qc1_title' },
  { slug: 'oopbuy-spreadsheet-vergleich', labelKey: 'articles.compare1_title' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 h-[72px] flex items-center transition-all duration-300 ${
        scrolled ? 'bg-background-50 shadow-[0_1px_3px_rgba(0,0,0,0.04)]' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 flex items-center justify-between">
        {/* Left: Brand + Nav links */}
        <div className="flex items-center gap-6">
          <Link to="/" className="font-heading text-xl md:text-[20px] font-bold text-foreground-950 tracking-[-0.5px] whitespace-nowrap">
            Oopbuy Spreadsheet
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-2">
            <Link
              to="/"
              className="h-10 px-4 flex items-center rounded-lg text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              {t('nav.home')}
            </Link>

            {/* Shopping Guide dropdown */}
            <div className="relative" ref={guideRef}>
              <button
                onClick={() => { setGuideDropdownOpen(!guideDropdownOpen); setDropdownOpen(false); }}
                className="flex items-center gap-1.5 h-10 px-4 rounded-lg text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span>{t('nav.shopping_guide')}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.08)] py-2 z-50">
                  {guideArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/artikel/${article.slug}`}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                      onClick={() => setGuideDropdownOpen(false)}
                    >
                      {t(article.labelKey)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop right side */}
        <div className="hidden md:flex items-center gap-6" ref={dropdownRef}>
          <div className="relative">
            <button
              onClick={() => { setDropdownOpen(!dropdownOpen); setGuideDropdownOpen(false); }}
              className="flex items-center gap-2 h-10 px-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors text-sm text-foreground-700 whitespace-nowrap cursor-pointer"
            >
              <span>{t('nav.category')}</span>
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.08)] py-2 z-50 max-h-[400px] overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    {t(cat.nameKey)}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="h-11 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold flex items-center gap-1.5 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('nav.cta1')}
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="h-11 px-5 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold flex items-center gap-1.5 hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('nav.cta2')}
            <i className="ri-shopping-bag-line text-sm"></i>
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden flex items-center justify-center w-10 h-10 text-foreground-700 cursor-pointer"
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed top-[72px] left-0 right-0 bottom-0 bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {/* Home link */}
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-4-line text-lg text-foreground-500"></i>
              {t('nav.home')}
            </Link>

            {/* Shopping Guide section */}
            <div className="pt-3">
              <button
                onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-book-open-line text-lg text-foreground-500"></i>
                  {t('nav.shopping_guide')}
                </span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${mobileGuideOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileGuideOpen && (
                <div className="ml-4 mt-1 border-l-2 border-background-200 pl-4 space-y-0.5">
                  {guideArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/artikel/${article.slug}`}
                      className="block px-4 py-2.5 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
                      onClick={() => { setMobileMenuOpen(false); setMobileGuideOpen(false); }}
                    >
                      {t(article.labelKey)}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Categories section */}
            <div className="pt-3">
              <p className="flex items-center gap-3 px-4 py-3 text-xs text-foreground-400 uppercase tracking-wider font-semibold">
                <i className="ri-grid-line text-sm text-foreground-400"></i>
                {t('nav.category')}
              </p>
              <div className="ml-4 mt-1 border-l-2 border-background-200 pl-4 grid grid-cols-2 gap-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors truncate"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t(cat.nameKey)}
                  </a>
                ))}
              </div>
            </div>

            {/* CTA buttons */}
            <div className="pt-5 space-y-3 px-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full h-12 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                {t('nav.cta1')}
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full h-12 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer"
              >
                {t('nav.cta2')}
                <i className="ri-shopping-bag-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}